import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Modal, ModalBody, ModalFooter } from 'reactstrap';
import FeatherIcon from 'feather-icons-react';
import Swal from 'sweetalert2';
import { archiveMastersheetItem } from './TenantForm/Helpers/api';
import { buildUnitChargeSummary, formatCurrency, formatLeaseRange } from './unitChargeSummary';

const UnitDetailsModal = ({
  open,
  onClose,
  row,
  tenantName,
  feeColumns,
  propertyId,
  buildingName,
  onEditTenant,
  onDataUpdate,
}) => {
  if (!row) return null;

  const unitNo = row.unitNo || 'N/A';
  const hasTenant = !!tenantName && tenantName !== 'N/A';
  const { lines, total, deposit, lease } = buildUnitChargeSummary(row, feeColumns);
  const leaseLabel = formatLeaseRange(lease);

  const handleAction = (action, e) => {
    e.stopPropagation();
    onClose();
    if (action === 'edit') {
      if (onEditTenant) onEditTenant({ ...row, propertyId, buildingName });
    } else if (action === 'manageLease') {
      const leasePath = `/leases/new_lease/${unitNo}/${propertyId}/${buildingName || ''}`;
      window.open(leasePath, '_blank');
    }
  };

  const handleArchive = async (e) => {
    e.stopPropagation();

    const { value: archivedReason, isConfirmed } = await Swal.fire({
      title: 'Are you sure you want to archive this item?',
      text: 'This action cannot be undone. Archived tenant records can be viewed in the Archive Mastersheet for future reference.',
      input: 'text',
      inputLabel: 'Reason for archiving',
      inputPlaceholder: 'Enter reason here...',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, archive it!',
      cancelButtonText: 'Cancel',
      inputValidator: (value) => {
        if (!value) return 'Reason is required';
      },
      customClass: { container: 'archive-dialog' },
    });

    if (!isConfirmed) return;

    Swal.fire({
      title: 'Archiving Unit',
      text: `Archiving Unit ${unitNo}...`,
      icon: 'info',
      showConfirmButton: false,
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      const { success, data } = await archiveMastersheetItem(
        row.mastersheet_id,
        propertyId,
        buildingName,
        unitNo,
        archivedReason
      );

      if (!success) throw new Error(data?.message || 'Failed to archive unit');

      await Swal.fire({
        title: 'Success!',
        text: data?.message || 'Unit has been archived successfully.',
        icon: 'success',
        confirmButtonText: 'OK',
      });

      onClose();
      if (onDataUpdate) onDataUpdate();
    } catch (error) {
      console.error('Error archiving unit:', error);
      await Swal.fire({
        title: 'Error!',
        text: error.message || 'Failed to archive unit. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK',
      });
    }
  };

  return (
    <Modal isOpen={open} toggle={onClose} centered size="md" fade={false} className="unit-dlg">
      <div className="unit-dlg__head">
        <div className="unit-dlg__head-main">
          <div className="unit-dlg__unit">🏠 Unit {unitNo}</div>
          <div className="unit-dlg__tenant">{hasTenant ? tenantName : 'No tenant assigned'}</div>
        </div>
        <div className="unit-dlg__head-right">
          <span className={`unit-dlg__pill ${hasTenant ? 'unit-dlg__pill--occupied' : 'unit-dlg__pill--vacant'}`}>
            {hasTenant ? 'Occupied' : 'Vacant'}
          </span>
          <button type="button" className="unit-dlg__close" aria-label="Close" onClick={onClose}>×</button>
        </div>
      </div>

      <ModalBody className="unit-dlg__body">
        {hasTenant ? (
          <>
            <div className="unit-dlg__section-label">Monthly Charges</div>
            {lines.length > 0 ? (
              lines.map((line) => (
                <div className="unit-dlg__row" key={line.key}>
                  <span className="unit-dlg__row-label">{line.label}</span>
                  <span className="unit-dlg__row-amt">{formatCurrency(line.amount)}</span>
                </div>
              ))
            ) : (
              <div className="unit-dlg__row unit-dlg__row--empty">No charges on record.</div>
            )}
            <div className="unit-dlg__total">
              <span className="unit-dlg__total-label">Total Monthly</span>
              <span className="unit-dlg__total-amt">{formatCurrency(total)}</span>
            </div>
            {(deposit != null || leaseLabel) && (
              <div className="unit-dlg__meta">
                {deposit != null && (
                  <span className="unit-dlg__meta-item">
                    Deposit<b>{formatCurrency(deposit)}</b>
                  </span>
                )}
                {leaseLabel && (
                  <span className="unit-dlg__meta-item">
                    Lease<b>{leaseLabel}</b>
                  </span>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="unit-dlg__empty">
            <div className="unit-dlg__empty-icon">🏚️</div>
            <div className="unit-dlg__empty-title">This unit is vacant</div>
            <div className="unit-dlg__empty-text">
              No tenant or charges on record yet. Assign a tenant or start a lease to get going.
            </div>
          </div>
        )}
      </ModalBody>

      <ModalFooter className="unit-dlg__foot">
        <Button color="warning" className="unit-dlg__btn" onClick={(e) => handleAction('edit', e)}>
          {hasTenant ? '✏️ Edit Tenant' : '✏️ Add Tenant'}
        </Button>
        <Button color="primary" className="unit-dlg__btn" onClick={(e) => handleAction('manageLease', e)}>
          {hasTenant ? '📋 Manage Lease' : '➕ Manage Lease'}
        </Button>
        {hasTenant && (
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id="archive-tooltip" style={{ zIndex: 2000 }}>
                Archive: irreversible, but archived records remain viewable in the Archive Mastersheet.
              </Tooltip>
            }
          >
            <button
              type="button"
              className="unit-dlg__archive"
              aria-label="Archive"
              onClick={handleArchive}
            >
              <FeatherIcon icon="hard-drive" size={16} />
            </button>
          </OverlayTrigger>
        )}
      </ModalFooter>
    </Modal>
  );
};

export { UnitDetailsModal };
